import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import lbDialog from '../../components/UI/Modals/lbDialog';
import { BadgeCheck, Copy, Crosshair, Gift, HelpCircle, Layers, Minus, Plus, Trophy, Users } from 'lucide-react';
import Spinner from '../../components/UI/Spinner';
import AdvancedBetting from './AdvancedBetting';
import BtcChart from './BtcChart';
import { AddressLink, Countdown, PredictionTicket, StatTile, TierBadge, btc, usd } from './components';
import { REFERRAL_TIERS, explorerTx } from './config';
import usdtIcon from '../../assets/utility/usdt.png';
import wbtcIcon from '../../assets/utility/bitcoin.png';
import { useT } from '../../i18n';
import { referralLink as platformLink, storedReferrer } from '../../referral';

const ZERO = '0x0000000000000000000000000000000000000000';
const isAddress = (value) => typeof value === 'string' && /^0x[a-fA-F0-9]{40}$/.test(value);

/** Dollar value of a WBTC amount (WBTC tracks BTC, priced by the same Chainlink feed) */
const wbtcUsd = (amount, btcPrice) => {
  if (!btcPrice || !Number.isFinite(amount)) return null;
  const value = amount * btcPrice;
  if (value > 0 && value < 0.01) return '< $0.01';
  return `$${usd(value, 2)}`;
};

/** Offsets offered as one-tap predictions around the live rounded price */
const QUICK_OFFSETS = [-100, -50, 0, 50, 100];

/**
 * Prize tier a prediction would win against `reference` (the closing price): 0 for the exact dollar, then the
 * first band whose distance in bps of the price covers it; null outside every band
 */
const tierFor = (prediction, reference, tiers) => {
  if (!prediction || !reference) return null;
  const distance = Math.abs(prediction - reference);
  if (distance === 0) return 0;
  for (let i = 1; i < tiers.length; i += 1) {
    if (distance * 10000 <= reference * tiers[i].bps) return i;
  }
  return null;
};

/** Dollar width of each band around the current price */
const bandDollars = (tier, price) => (price && tier.bps ? Math.floor((price * tier.bps) / 10000) : 0);

const buildFaq = (t, tiers) => [
  {
    q: t('bullseye.faqWinnersQ'),
    a: (
      <>
        <p>{t('bullseye.faq.winnersIntro')}</p>
        <ul className="mt-2 space-y-1">
          {tiers.map((tier) => (
            <li key={tier.margin} className="flex items-center justify-between gap-3">
              <span className="text-white/70">
                {tier.margin === 0 ? t('bullseye.faq.exactPrice') : t('bullseye.faq.difference', { margin: tier.short })}
              </span>
              <span className="notranslate lb-num font-bold text-white">
                {tier.share}% {t('bullseye.faq.ofThePool')}
                {tier.jackpot ? t('bullseye.faq.plusJackpot') : ''}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-2">{t('bullseye.faq.rollover')}</p>
      </>
    ),
  },
  { q: t('bullseye.faqBonusQ'), a: <p>{t('bullseye.faqBonusA')}</p> },
  { q: t('bullseye.faqJackpotQ'), a: <p>{t('bullseye.faq.jackpotA')}</p> },
  { q: t('bullseye.faqPriceQ'), a: <p>{t('bullseye.faq.priceA')}</p> },
];

const Panel = ({ title, icon: Icon, children, action = null, className = '', collapsible = false }) => {
  const heading = (
    <h3 className="lb-heading flex items-center gap-2 text-lg">
      {Icon ? <Icon className="h-5 w-5 text-brand-cyan" aria-hidden="true" /> : null}
      {title}
    </h3>
  );
  if (collapsible) {
    return (
      <details className={`lb-card group ${className}`}>
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-5">
          {heading}
          <span className="text-white/40 transition-transform group-open:rotate-180" aria-hidden="true">
            ▾
          </span>
        </summary>
        <div className="px-5 pb-5">{children}</div>
      </details>
    );
  }
  return (
    <section className={`lb-card p-5 ${className}`}>
      <header className="mb-4 flex items-center justify-between gap-3">
        {heading}
        {action}
      </header>
      {children}
    </section>
  );
};

/** What each distance from the closing price pays, in dollars around the current price */
const PrizeLadder = ({ tiers, price, draft, t }) => {
  const reference = price;
  const draftTier = tierFor(Number(draft), reference, tiers);
  const distance = draft && reference ? Math.abs(Math.round(Number(draft)) - reference) : null;
  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
      <p className="lb-label mb-3">{t('bullseye.prizeLadder')}</p>
      <ul className="space-y-1.5">
        {tiers.map((tier, index) => {
          const active = draftTier === index;
          return (
            <li
              key={tier.margin}
              className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm transition-colors ${
                active ? 'border border-brand-cyan/50 bg-brand-cyan/10' : 'border border-transparent'
              }`}
            >
              <span className="flex items-center gap-2 text-white/75">
                <TierBadge tier={index} />
                <span className="notranslate lb-num">
                  {index === 0
                    ? t('bullseye.ladderExact')
                    : t('bullseye.ladderWithin', { amount: usd(bandDollars(tier, price)) })}
                </span>
              </span>
              <span className="notranslate lb-num text-right font-bold text-white">
                {t('bullseye.ladderShare', { share: tier.share })}
                {tier.jackpot ? <span className="block text-[11px] font-semibold text-amber-300">{t('bullseye.ladderJackpot')}</span> : null}
              </span>
            </li>
          );
        })}
      </ul>
      {distance !== null && Number.isFinite(distance) ? (
        <p className="mt-3 text-xs text-white/55">
          {t('bullseye.ladderNow', { distance: `$${usd(distance)}` })}
        </p>
      ) : null}
      <p className="mt-1 text-[11px] text-white/35">{t('bullseye.ladderNote')}</p>
    </div>
  );
};


const Game = ({ game }) => {
  const {
    address,
    isConnected,
    loading,
    error,
    liveRound,
    liveRoundId,
    nextRoundId,
    btcPrice,
    roundedPrice,
    jackpot,
    pool,
    betAmount,
    roundDuration,
    fees,
    prevStats,
    prevWinners,
    myLiveBets,
    myNextBets,
    allowance,
    usdtBalance,
    referral,
    tiers,
    approveUsdt,
    placeBet,
  } = game;

  const t = useT();
  const [searchParams] = useSearchParams();
  const [draft, setDraft] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [busy, setBusy] = useState(null);
  const [showPrizes, setShowPrizes] = useState(false);
  const [showMany, setShowMany] = useState(false);

  // start from the live rounded price instead of an empty field
  useEffect(() => {
    setDraft((current) => (current === '' && roundedPrice ? String(roundedPrice) : current));
  }, [roundedPrice]);

  const referrerFromUrl = useMemo(() => {
    const value = searchParams.get('ref') || searchParams.get('r') || searchParams.get('referral') || storedReferrer(address) || '';
    return isAddress(value) ? value : null;
  }, [searchParams, address]);
  // a referrer is only recorded on the first bet; after that the contract keeps the one it stored
  const activeReferrer = referral.referrer || referrerFromUrl || ZERO;

  // the draft counts as a prediction when the list is empty, so one tap on the main button is enough
  const draftPrice = Math.round(Number(draft));
  const draftValid = Number.isFinite(draftPrice) && draftPrice > 0;
  const toSend = predictions.length ? predictions : draftValid ? [draftPrice] : [];
  const total = toSend.length * (betAmount || 0);
  const needsApproval = total > 0 && allowance < total;
  const notEnough = isConnected && total > usdtBalance;
  const hours = roundDuration ? Math.round(roundDuration / 3600) : 4;
  const referralLink = platformLink(address);
  const currentTier = REFERRAL_TIERS.find((tier) => referral.count >= tier.min) || REFERRAL_TIERS[REFERRAL_TIERS.length - 1];
  const jackpotUsd = wbtcUsd(jackpot, btcPrice);
  const hasTickets = myLiveBets.length > 0 || (nextRoundId !== liveRoundId && myNextBets.length > 0);

  const addPrediction = (value) => {
    const price = Math.round(Number(value));
    if (!Number.isFinite(price) || price <= 0) {
      lbDialog.fire({ icon: 'warning', title: t('bullseye.enterWholeDollar'), timer: 1800, showConfirmButton: false });
      return;
    }
    if (predictions.includes(price)) {
      lbDialog.fire({ icon: 'info', title: t('bullseye.alreadyAdded'), text: t('bullseye.alreadyInList'), timer: 1800, showConfirmButton: false });
      return;
    }
    setPredictions((list) => [...list, price].sort((a, b) => a - b));
  };
  const removePrediction = (price) => setPredictions((list) => list.filter((value) => value !== price));
  const addMany = (prices) => {
    if (!prices.length) return;
    setPredictions((list) => [...list, ...prices].sort((a, b) => a - b));
    setShowMany(false);
    lbDialog.fire({ icon: 'success', title: t('bullseye.ticketsAdded', { count: prices.length }), timer: 1400, showConfirmButton: false });
  };
  const nudge = (delta) => setDraft((current) => String(Math.max(1, (Math.round(Number(current)) || roundedPrice || 0) + delta)));

  const runApprove = async () => {
    setBusy('approve');
    try {
      const hash = await approveUsdt(total);
      lbDialog.fire({
        icon: 'success',
        title: t('bullseye.approved'),
        html: hash ? `<a class="lb-link" href="${explorerTx(hash)}" target="_blank" rel="noopener noreferrer">${t('bullseye.viewTransaction')}</a>` : undefined,
      });
    } catch (err) {
      lbDialog.fire({ icon: 'error', title: t('bullseye.approvalFailed'), text: err?.shortMessage || err?.message });
    } finally {
      setBusy(null);
    }
  };

  const runBet = async () => {
    setBusy('bet');
    try {
      const hash = await placeBet(toSend, activeReferrer);
      setPredictions([]);
      lbDialog.fire({
        icon: 'success',
        title: t('bullseye.predictionsPlaced'),
        html: hash ? `<a class="lb-link" href="${explorerTx(hash)}" target="_blank" rel="noopener noreferrer">${t('bullseye.viewTransaction')}</a>` : undefined,
      });
    } catch (err) {
      lbDialog.fire({ icon: 'error', title: t('bullseye.betFailed'), text: err?.shortMessage || err?.message });
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="py-24">
        <Spinner size="lg" label={t('bullseye.loading')} />
        <p className="mt-6 text-center text-sm text-white/50">{t('bullseye.readingContract')}</p>
      </div>
    );
  }

  const steps = [t('bullseye.step1'), t('bullseye.step2'), t('bullseye.step3', { hours })];

  return (
    <div className="space-y-6">
      {error ? (
        <div className="rounded-2xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-200">{error}</div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[1.4fr,1fr]">
        {/* ---------- 1. the prediction: deadline, prize, reference price, input, action ---------- */}
        <section className="rounded-3xl border-2 border-brand-cyan/40 bg-gradient-to-b from-[#06202a] to-[#06030a] p-5 shadow-[0_0_60px_-24px_rgba(34,211,238,0.8)] sm:p-7">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 className="lb-heading text-2xl text-white sm:text-3xl">{t('bullseye.simpleTitle')}</h2>
            <div className="text-right">
              <p className="lb-label">{t('bullseye.predictionsClose')}</p>
              <Countdown endTime={liveRound.endTime} className="font-display text-2xl lb-gradient-text" />
            </div>
          </div>

          {/* what is at stake: the pot and the jackpot, each with its coin */}
          <div className="mt-5 grid grid-cols-2 divide-x divide-white/10 rounded-2xl border border-white/10 bg-white/[0.04]">
            <div className="flex items-center gap-3 p-4">
              <img src={usdtIcon} alt="USDT" className="h-9 w-9 shrink-0 rounded-full" />
              <div className="min-w-0">
                <p className="lb-label">{t('bullseye.potNow')}</p>
                <p className="notranslate lb-num font-display text-2xl leading-tight text-white">
                  {usd(pool, 2)} <span className="text-sm text-white/50">USDT</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4">
              <img src={wbtcIcon} alt="WBTC" className="h-9 w-9 shrink-0 rounded-full" />
              <div className="min-w-0">
                <p className="lb-label">{t('bullseye.wbtcJackpot')}</p>
                <p className="notranslate lb-num font-display text-2xl leading-tight text-amber-300">
                  {btc(jackpot)} <span className="text-sm text-white/50">WBTC</span>
                </p>
                {jackpotUsd ? <p className="notranslate lb-num text-xs text-white/45">≈ {jackpotUsd}</p> : null}
              </div>
            </div>
          </div>

          {/* the guess, with the live price as its reference */}
          <div className="mt-6">
            <div className="mb-2 flex items-end justify-between gap-3">
              <label className="lb-label" htmlFor="bullseye-price">
                {t('bullseye.predictedBitcoinPrice')}
              </label>
              <p className="notranslate text-xs text-white/55">
                {t('bullseye.btcNow')} <span className="lb-num font-bold text-white">${usd(btcPrice, 2)}</span>
              </p>
            </div>
            <div className="flex items-stretch gap-2">
              <button type="button" onClick={() => nudge(-1)} aria-label="-1" className="lb-btn lb-btn-outline px-4">
                <Minus className="h-5 w-5" />
              </button>
              <div className="relative flex-1">
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-white/40">$</span>
                <input
                  id="bullseye-price"
                  type="number"
                  inputMode="numeric"
                  step="1"
                  min="1"
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') addPrediction(draft);
                  }}
                  placeholder={t('bullseye.wholeDollarsHint')}
                  className="lb-input notranslate lb-num w-full py-4 pl-10 text-center text-3xl font-bold"
                />
              </div>
              <button type="button" onClick={() => nudge(1)} aria-label="+1" className="lb-btn lb-btn-outline px-4">
                <Plus className="h-5 w-5" />
              </button>
            </div>
            {roundedPrice ? (
              <div className="mt-3 flex flex-wrap justify-center gap-1.5">
                {QUICK_OFFSETS.map((offset) => (
                  <button
                    key={offset}
                    type="button"
                    onClick={() => setDraft(String(roundedPrice + offset))}
                    className={`lb-chip notranslate lb-num hover:border-white/30 ${
                      draftPrice === roundedPrice + offset ? 'border-brand-cyan/60 text-white' : ''
                    }`}
                  >
                    {offset === 0 ? `$${usd(roundedPrice)}` : `${offset > 0 ? '+' : '−'}$${Math.abs(offset)}`}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {/* tickets waiting to be sent */}
          {predictions.length ? (
            <div className="mt-6">
              <div className="mb-3 flex items-center justify-between">
                <p className="lb-label">{t('bullseye.yourPredictionsList', { n: predictions.length, amount: usd(total, 2) })}</p>
                <button type="button" onClick={() => setPredictions([])} className="lb-link text-xs">
                  {t('bullseye.clearAll')}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {predictions.map((price, index) => (
                  <PredictionTicket
                    key={`draft-${price}`}
                    price={price}
                    roundId={nextRoundId}
                    index={index}
                    onRemove={() => removePrediction(price)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {/* the action */}
          <div className="mt-6 space-y-3">
            {!isConnected ? (
              <div className="flex flex-col items-center gap-2 rounded-xl border border-white/10 bg-black/30 p-4">
                <p className="text-sm text-white/60">{t('bullseye.connectToPlace')}</p>
                <appkit-button />
              </div>
            ) : needsApproval ? (
              <button type="button" onClick={runApprove} disabled={busy !== null || notEnough} className="lb-btn lb-btn-primary lb-btn-lg w-full">
                {busy === 'approve' ? t('bullseye.approving') : t('bullseye.approveFirst', { amount: usd(total, 2) })}
              </button>
            ) : (
              <button
                type="button"
                onClick={runBet}
                disabled={busy !== null || !toSend.length || notEnough}
                className="lb-btn lb-btn-primary lb-btn-lg w-full"
              >
                {busy === 'bet' ? t('bullseye.placing') : notEnough ? t('bullseye.needUsdt') : t('bullseye.predictFor1', { amount: usd(total, 2) })}
              </button>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
              <div className="flex gap-4">
                <button type="button" onClick={() => addPrediction(draft)} className="lb-link">
                  + {t('bullseye.addAnother')}
                </button>
                <button type="button" onClick={() => setShowMany((open) => !open)} className="lb-link inline-flex items-center gap-1">
                  <Layers className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('bullseye.manyShort')}
                </button>
              </div>
              {isConnected ? <span className="notranslate text-white/45">{t('bullseye.balanceLine', { amount: usd(usdtBalance, 2) })}</span> : null}
            </div>
            {showMany ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <AdvancedBetting basePrice={roundedPrice} betAmount={betAmount} existing={predictions} onAdd={addMany} />
              </div>
            ) : null}
            {referrerFromUrl && !referral.referrer ? (
              <p className="text-xs text-white/45">
                {t('bullseye.referredBy')} <AddressLink value={referrerFromUrl} />, {t('bullseye.referredByNote')}
              </p>
            ) : null}
          </div>

          {/* how the prize works, in one line; the table on demand */}
          <div className="mt-5 border-t border-white/10 pt-4">
            <p className="text-sm text-white/60">
              {t('bullseye.closerWins')}{' '}
              <button type="button" onClick={() => setShowPrizes((open) => !open)} className="lb-link">
                {showPrizes ? t('bullseye.hidePrizes') : t('bullseye.seePrizes')}
              </button>
            </p>
            {showPrizes ? (
              <div className="mt-3">
                <PrizeLadder tiers={tiers} price={roundedPrice} draft={draft} t={t} />
              </div>
            ) : null}
          </div>
        </section>

        {/* ---------- 2. what the bettor already has in play, then the market ---------- */}
        <div className="space-y-6">
          {isConnected && hasTickets ? (
            <Panel title={t('bullseye.yourTicketsInPlay')} icon={Crosshair}>
              <div className="space-y-5">
                {myLiveBets.length ? (
                  <div>
                    <p className="lb-label mb-3">
                      {t('bullseye.liveRoundLabel', { round: liveRoundId })} · {t('bullseye.ifEndedNow')}
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      {myLiveBets.map((price, index) => (
                        <PredictionTicket
                          key={`live-${price}-${index}`}
                          price={price}
                          roundId={liveRoundId}
                          index={index}
                          tier={tierFor(price, roundedPrice, tiers)}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
                {nextRoundId !== liveRoundId && myNextBets.length ? (
                  <div>
                    <p className="lb-label mb-3">{t('bullseye.nextRoundLabel', { round: nextRoundId })}</p>
                    <div className="grid grid-cols-2 gap-3">
                      {myNextBets.map((price, index) => (
                        <PredictionTicket key={`next-${price}-${index}`} price={price} roundId={nextRoundId} index={index} />
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </Panel>
          ) : null}
          <BtcChart price={btcPrice} roundedPrice={roundedPrice} />
        </div>
      </div>

      {/* ---------- 3. everything else, closed until asked for ---------- */}
      <div className="grid items-start gap-4 md:grid-cols-2">
        <Panel title={t('bullseye.previousRound')} icon={Trophy} collapsible>
          {prevStats && prevStats.roundId ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <StatTile label={t('bullseye.round')} value={`#${prevStats.roundId}`} />
                <StatTile
                  label={t('bullseye.finalPrice')}
                  value={prevStats.price ? `$${usd(prevStats.price)}` : t('bullseye.pendingLabel')}
                  tone="gold"
                />
              </div>
              {prevWinners.length ? (
                <div className="lb-scroll mt-4 max-h-56 overflow-y-auto rounded-2xl border border-white/10">
                  <table className="w-full text-left text-sm">
                    <thead className="sticky top-0 bg-black/80 backdrop-blur">
                      <tr className="text-[0.65rem] uppercase tracking-[0.16em] text-white/45">
                        <th className="px-3 py-2 font-normal">{t('bullseye.player')}</th>
                        <th className="px-3 py-2 font-normal">{t('bullseye.prediction')}</th>
                        <th className="px-3 py-2 text-right font-normal">{t('bullseye.prize')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prevWinners.map((winner, index) => (
                        <tr key={`${winner.player}-${index}`} className="border-t border-white/5 text-white/80">
                          <td className="px-3 py-2">
                            <AddressLink value={winner.player} />
                          </td>
                          <td className="notranslate lb-num px-3 py-2">
                            ${usd(winner.price)} <TierBadge tier={winner.tier} />
                          </td>
                          <td className="notranslate lb-num px-3 py-2 text-right">
                            <span className="font-bold text-white">{usd(winner.usdt, 2)} USDT</span>
                            {winner.wbtc > 0 ? <span className="block text-xs text-amber-300">+ {btc(winner.wbtc)} WBTC</span> : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-center text-sm text-white/50">
                  {t('bullseye.noWinnersRolled')}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-white/50">{t('bullseye.noRoundResolved')}</p>
          )}
        </Panel>

        <Panel title={t('bullseye.howItWorks')} icon={HelpCircle} collapsible>
          <ol className="mb-4 space-y-2">
            {steps.map((text, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-white/65">
                <span className="lb-num flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-cyan text-[10px] font-bold text-black">
                  {i + 1}
                </span>
                {text}
              </li>
            ))}
          </ol>
          <div className="space-y-2">
            {buildFaq(t, tiers).map((item) => (
              <details key={item.q} className="group rounded-2xl border border-white/10 bg-white/[0.03]">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-white">
                  {item.q}
                  <span className="text-white/40 transition-transform group-open:rotate-180" aria-hidden="true">
                    ▾
                  </span>
                </summary>
                <div className="border-t border-white/5 px-4 py-3 text-sm text-white/60">{item.a}</div>
              </details>
            ))}
          </div>
        </Panel>

        <Panel title={t('bullseye.referEarn')} icon={Users} collapsible>
          {isConnected ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <StatTile label={t('bullseye.yourRate')} value={`${referral.rate}%`} tone="gradient" />
                <StatTile label={t('bullseye.referred')} value={usd(referral.count)} />
              </div>
              {referral.earned !== null ? (
                <div className="mt-3">
                  <StatTile label={t('bullseye.earned')} value={`${usd(referral.earned, 2)} USDT`} tone="gold" />
                </div>
              ) : null}
              <div className="mt-5">
                <p className="lb-label mb-2">{t('bullseye.commissionTiers')}</p>
                <ul className="space-y-1.5 text-sm">
                  {REFERRAL_TIERS.map((tier) => (
                    <li
                      key={tier.name}
                      className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2 ${
                        tier === currentTier ? 'border border-brand-cyan/30 bg-brand-cyan/10 text-white' : 'text-white/50'
                      }`}
                    >
                      <span>
                        {tier.name} ({tier.min}+)
                      </span>
                      <span className="notranslate lb-num font-bold">{tier.rate}%</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-5">
                <p className="lb-label mb-2">{t('bullseye.yourLink')}</p>
                <div className="flex gap-2">
                  <input readOnly value={referralLink} className="lb-input notranslate flex-1 text-xs" onFocus={(event) => event.target.select()} />
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(referralLink);
                      lbDialog.fire({ icon: 'success', title: t('bullseye.linkCopied'), timer: 1200, showConfirmButton: false });
                    }}
                    className="lb-btn lb-btn-outline lb-btn-sm shrink-0"
                    aria-label={t('bullseye.copyReferralLink')}
                  >
                    <Copy className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
                <Link to="/referral" className="lb-link mt-2 inline-block text-xs">
                  {t('referral.seeAll')}
                </Link>
              </div>
              {referral.firstTime ? (
                <p className="mt-4 flex items-start gap-2 rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 py-2.5 text-xs text-amber-200">
                  <Gift className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  {t('bullseye.firstBetBonus')}
                </p>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-white/50">{t('bullseye.referralConnect', { pct: 2 })}</p>
          )}
        </Panel>

        {fees ? (
          <Panel title={t('bullseye.whereEachBetGoes')} icon={BadgeCheck} collapsible>
            <ul className="space-y-2.5 text-sm">
              {[
                [t('bullseye.roundPot'), fees.round],
                [t('bullseye.wbtcJackpot'), fees.jackpot],
                [t('bullseye.liquidityMining'), fees.staking],
                [t('bullseye.house'), fees.house],
                [t('bullseye.rolloverShare'), fees.rollover],
                [t('bullseye.nodeShare'), fees.node],
                [t('bullseye.monthlyLeaderboard'), fees.monthly],
              ]
                .filter(([, value]) => value !== null)
                .map(([label, value]) => (
                  <li key={label}>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-white/60">{label}</span>
                      <span className="notranslate lb-num font-bold text-white">{value}%</span>
                    </div>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/10">
                      <div className="h-full rounded-full lb-fill-brand" style={{ width: `${value}%` }} />
                    </div>
                  </li>
                ))}
            </ul>
          </Panel>
        ) : null}
      </div>
    </div>
  );
};

export default Game;
